import { InfoLogs } from '@/constants/logs';
import { logInfo, logWarn } from '@/utils/logger';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import type { ICheckoutContext } from '@/types/context';

/**
 * Conversion-diagnostics instrumentation for the present → pay window, emitted
 * as Coralogix RUM logs.
 *
 * Motivation: the funnel was blind between checkout being presented and the pay
 * click. On the Android `default` template ~91% of players land on the manual
 * card form and only ~41% reach pay-click (vs iOS riding Apple Pay), with no
 * signal for *where* in that ~78s they abandon. These logs localize the drop:
 * whether express wallets ever became usable, and how far card players get
 * (start → field error → ready-to-pay → blocked tap).
 *
 * Where the fields live: Coralogix RUM is configured with
 * `stringifyCustomLogData: true`, which flattens the `data` object into a
 * single stringified blob. So the *queryable* segmentation dimensions (OS,
 * template, payment method, wallet state, …) are passed as **labels**, which
 * stay individually filterable in Coralogix. `getMixpanelProperties` is reused
 * purely as the (already battle-tested) extractor for those dimensions — no
 * event is sent to Mixpanel from here.
 *
 * Session semantics: one checkout = one app load. The "first occurrence" logs
 * use module-scoped guards so re-mounts, template fallbacks, and re-renders
 * don't double-count.
 */

export type ExpressWalletState = 'ready' | 'unavailable';
export type CardFieldName = 'number' | 'expiry' | 'cvc' | 'email' | 'zip';

/** Native wallets we track availability for individually. */
export type ExpressWalletName = 'apple_pay' | 'google_pay' | 'amazon_pay';

interface ExpressWalletAvailabilityInput {
  /** Which native wallet this report is about (Apple Pay vs Google Pay). */
  wallet: ExpressWalletName;
  /** PSP that rendered it: `stripe` | `adyen`. */
  provider: string;
  /** True when the wallet resolved usable/painted; false when it didn't. */
  is_available: boolean;
  /**
   * Why it was unavailable, when the site knows it:
   * `device_unsupported` | `load_error` | `init_error` |
   * `client_ece_hang_timeout` (OUR hang-breaker — not a Stripe SDK timeout) |
   * `timeout` (template deferred-reveal budget).
   * Free-form so a new cause doesn't require a type bump.
   */
  reason?: string;
  /** Wallet mount → this resolution, in ms. */
  load_time_ms?: number;
  /**
   * When `reason` is a client hang-breaker, the armed budget in ms so RUM
   * can distinguish "we gave up after N ms" from Stripe-reported failures.
   */
  timeout_ms?: number;
}

type Labels = Record<string, string | number | boolean>;

interface ExpressWalletResolvedPayload {
  express_key: string;
  wallet_provider: string;
  wallet_state: ExpressWalletState;
  resolve_time_ms: number;
}

interface ApplePayLoadedPayload {
  payment_provider: string;
  load_time_ms: number;
}

/** Above this, an Apple Pay load is "stuck" and worth an explained Warn log. */
export const SLOW_APPLE_PAY_THRESHOLD_MS = 5000;

export type ApplePayLoadOutcome = 'ready' | 'unavailable' | 'error';

/** Per-phase durations (ms). Keys differ per provider (see reason tables). */
export type ApplePayLoadPhases = Record<string, number>;

interface ApplePaySlowLoadInput {
  payment_provider: string;
  phases: ApplePayLoadPhases;
  total_ms: number;
  outcome?: ApplePayLoadOutcome;
}

/**
 * Human-readable attribution per dominant phase. The phase KEYS are the
 * contract between the instrumentation sites and these tables — keep them in
 * sync with `useAdyenApplePay` (Adyen) and the Stripe forms (Stripe).
 */
const STRIPE_PHASE_REASON: Record<string, string> = {
  stripe_load:
    'Stripe.js / loadStripe resolution dominated — the SDK was not warm (cache miss) or js.stripe.com was slow before the Express Checkout Element could mount.',
  ece_probe:
    'Stripe Express Checkout Element internal Apple Pay availability / merchant validation dominated — this runs inside the cross-origin js.stripe.com iframe (canMakePayment + Apple merchant session) and is not directly observable from the page.',
};

const ADYEN_PHASE_REASON: Record<string, string> = {
  sdk_init:
    'Adyen AdyenCheckout() instance init dominated — the Apple Pay checkout instance was cold (not prewarmed) and/or the checkoutshopper CDN (translations/config) was slow.',
  create_component:
    'Adyen ApplePay component construction dominated — @adyen/adyen-web chunk parse / component instantiation.',
  is_available:
    'Adyen ApplePay isAvailable() dominated — device/merchant validation (canMakePaymentsWithActiveCard / Apple merchant session) was slow.',
};

/**
 * Pure: given per-phase timings, pick the phase that took longest and map it
 * to a provider-specific explanation. Isolated from the RUM side-effect so it
 * can be unit-tested exhaustively.
 */
export const diagnoseApplePaySlowLoad = (
  provider: string,
  phases: ApplePayLoadPhases
): { dominant_phase: string; reason: string } => {
  const entries = Object.entries(phases).filter(([, v]) => typeof v === 'number' && Number.isFinite(v));
  if (entries.length === 0) {
    return { dominant_phase: 'unknown', reason: 'No phase timing was captured before the delay.' };
  }
  const [dominant_phase] = entries.reduce((max, cur) => (cur[1] > max[1] ? cur : max));
  const table = provider === 'adyen' ? ADYEN_PHASE_REASON : STRIPE_PHASE_REASON;
  const reason = table[dominant_phase] ?? `Longest phase was "${dominant_phase}".`;
  return { dominant_phase, reason };
};

let cardFormStartedFired = false;
let cardFormReadyToPayFired = false;
let applePayLoadedFired = false;
const expressWalletResolvedKeys = new Set<string>();
const cardFieldInvalidFields = new Set<CardFieldName>();
// One checkout = one app load. These per-wallet guards make the availability
// logs fire at most once per outcome (keyed `provider:wallet`) so re-mounts,
// StrictMode double-invokes and template fallbacks don't inflate counts — while
// still capturing a genuine ready→unavailable transition (the `ready` fires
// once AND the later `unavailable` fires once).
const expressWalletReadyKeys = new Set<string>();
const expressWalletUnavailableKeys = new Set<string>();

/**
 * Hard guarantee: diagnostics must NEVER break checkout. Every tracker runs
 * inside this guard, so a throw anywhere on the path (label extraction, the
 * Coralogix SDK, an exotic browser) is swallowed. `logInfo` is itself a no-op
 * until RUM inits and catches its own errors, so this is belt-and-suspenders
 * that also covers `buildLabels`. A lost diagnostics log is always preferable
 * to a broken payment flow.
 */
const safe = (fn: () => void): void => {
  try {
    fn();
  } catch {
    /* never let instrumentation surface into the checkout UI */
  }
};

/**
 * Lean, queryable label set built from the standard checkout dimensions plus
 * any event-specific extras. `undefined`/`null` are dropped (Coralogix labels
 * must be scalar) so partial context never throws.
 */
const buildLabels = (context: ICheckoutContext | undefined, extra: Labels = {}): Labels => {
  const props = getMixpanelProperties(context) as Record<string, unknown>;
  const labels: Labels = {};
  const add = (key: string, value: unknown): void => {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      labels[key] = value;
    }
  };
  add('os', props.OS);
  add('os_version', props.OS_version);
  add('device_type', props.device_type);
  add('browser', props.browser);
  add('template_type', props.template_type);
  add('payment_method', props.payment_method);
  add('payment_provider', props.payment_provider);
  add('publisher_id', props.publisher_id);
  add('session_id', props.session_id ?? context?.sessionId);
  add('environment', props.environment);
  add('checkout_type', props.checkout_type);
  for (const [key, value] of Object.entries(extra)) add(key, value);
  return labels;
};

/**
 * Fires once per express wallet (keyed by `express_key`) when it settles to
 * ready/unavailable. `resolve_time_ms` is measured from the wallet row mount,
 * so a never-resolving wallet simply never emits — which is itself the signal.
 */
export const trackExpressWalletResolved = (
  context: ICheckoutContext | undefined,
  payload: ExpressWalletResolvedPayload
): void =>
  safe(() => {
    if (expressWalletResolvedKeys.has(payload.express_key)) return;
    expressWalletResolvedKeys.add(payload.express_key);
    logInfo(InfoLogs.expressWalletResolved, { ...payload }, buildLabels(context, { ...payload }));
  });

/**
 * Per-wallet Apple Pay / Google Pay availability, emitted to Coralogix RUM so
 * we can answer — per session, segmentable by OS / publisher / template —
 * exactly what happened to each native wallet:
 *
 *   - it became usable                       → `Express wallet ready` (Info)
 *   - it never loaded, for ANY reason/error  → `Express wallet unavailable`
 *       (Warn) with `was_ready:false`
 *   - it was SHOWN and then vanished         → `Express wallet unavailable`
 *       (Warn) with `was_ready:true` / `disappeared:true`
 *
 * The "was shown then disappeared" case is the one the funnel was blind to:
 * `was_ready` is derived from whether THIS wallet already reported ready in the
 * same session, so a single label filter separates a disappearance from a
 * never-loaded. `reason` carries the cause when the call site knows it
 * (`device_unsupported` | `load_error` | `init_error` | `client_ece_hang_timeout` |
 * `timeout`).
 *
 * Deduped per `provider:wallet` per outcome so re-mounts don't spam, yet a real
 * ready→unavailable transition still emits both logs.
 */
export const trackExpressWalletAvailability = (
  context: ICheckoutContext | undefined,
  input: ExpressWalletAvailabilityInput
): void =>
  safe(() => {
    const key = `${input.provider}:${input.wallet}`;
    const extras: Labels = { wallet: input.wallet, payment_provider: input.provider };
    if (typeof input.load_time_ms === 'number' && Number.isFinite(input.load_time_ms)) {
      extras.load_time_ms = Math.max(0, Math.round(input.load_time_ms));
    }
    if (typeof input.timeout_ms === 'number' && Number.isFinite(input.timeout_ms)) {
      extras.timeout_ms = Math.max(0, Math.round(input.timeout_ms));
    }

    if (input.is_available) {
      if (expressWalletReadyKeys.has(key)) return;
      expressWalletReadyKeys.add(key);
      logInfo(InfoLogs.expressWalletReady, { ...extras, button_shown: true }, buildLabels(context, { ...extras, button_shown: true }));
      return;
    }

    if (expressWalletUnavailableKeys.has(key)) return;
    expressWalletUnavailableKeys.add(key);
    const wasReady = expressWalletReadyKeys.has(key);
    const reason = input.reason ?? 'unknown';
    const summary: Labels = {
      ...extras,
      was_ready: wasReady,
      // Explicit boolean so a dashboard can alert on the "seen then gone" case
      // without recomputing it from `was_ready`.
      disappeared: wasReady,
      // Queryable "we did not leave a usable wallet button on screen".
      button_shown: false,
      reason,
      // `reason` is dropped as a Coralogix custom-log label in prod (groupby
      // is null). `wallet_reason` is the queryable copy; keep `reason` in the
      // data blob so existing `data ~ '…'` filters still match.
      wallet_reason: reason,
    };
    logWarn(InfoLogs.expressWalletUnavailable, { ...summary }, buildLabels(context, { ...summary }));
  });

/**
 * Fires once per checkout when the Apple Pay button becomes ready for
 * interaction. `load_time_ms` is measured from the button mount being
 * initiated (the Apple Pay form mounting) to that ready signal, giving us
 * Apple Pay load duration that's segmentable by publisher / template / device.
 * A button that never becomes ready simply never emits — which is itself the
 * signal. Re-mounts (e.g. PREFERRED → DEFAULT fallback) are deduped so the
 * first real load is the one recorded.
 */
export const trackApplePayLoaded = (context: ICheckoutContext | undefined, payload: ApplePayLoadedPayload): void =>
  safe(() => {
    if (applePayLoadedFired) return;
    applePayLoadedFired = true;
    logInfo(InfoLogs.applePayLoaded, { ...payload }, buildLabels(context, { ...payload }));
  });

/**
 * Warn-level diagnostic emitted only when an Apple Pay button took longer than
 * {@link SLOW_APPLE_PAY_THRESHOLD_MS} to settle. Unlike the once-per-session
 * "loaded" log this is intentionally NOT deduped: each slow occurrence (incl.
 * re-init churn) is a distinct signal. Timings are rounded and forwarded both
 * in the data blob and — for the queryable subset — in labels.
 */
export const trackApplePaySlowLoad = (context: ICheckoutContext | undefined, input: ApplePaySlowLoadInput): void =>
  safe(() => {
    if (!Number.isFinite(input.total_ms) || input.total_ms < SLOW_APPLE_PAY_THRESHOLD_MS) return;

    const roundedPhases: Labels = {};
    for (const [key, value] of Object.entries(input.phases)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        roundedPhases[key] = Math.max(0, Math.round(value));
      }
    }

    const { dominant_phase, reason } = diagnoseApplePaySlowLoad(input.payment_provider, input.phases);
    const outcome = input.outcome ?? 'ready';
    const total_ms = Math.round(input.total_ms);

    const summary: Labels = {
      payment_provider: input.payment_provider,
      total_ms,
      outcome,
      dominant_phase,
      reason,
    };

    logWarn(InfoLogs.applePaySlowLoad, { ...summary, ...roundedPhases }, buildLabels(context, { ...summary }));
  });

/** First focus into any card field — proves the player engaged the card form. */
export const trackCardFormStarted = (context?: ICheckoutContext): void =>
  safe(() => {
    if (cardFormStartedFired) return;
    cardFormStartedFired = true;
    logInfo(InfoLogs.cardFormStarted, null, buildLabels(context));
  });

/** First validation error per field — surfaces which field blocks completion. */
export const trackCardFieldInvalid = (context: ICheckoutContext | undefined, field: CardFieldName): void =>
  safe(() => {
    if (cardFieldInvalidFields.has(field)) return;
    cardFieldInvalidFields.add(field);
    logInfo(InfoLogs.cardFieldInvalid, { field }, buildLabels(context, { field }));
  });

/** Card form first reached a payable (valid) state — the Pay button is now enabled. */
export const trackCardFormReadyToPay = (context?: ICheckoutContext): void =>
  safe(() => {
    if (cardFormReadyToPayFired) return;
    cardFormReadyToPayFired = true;
    logInfo(InfoLogs.cardFormReadyToPay, null, buildLabels(context));
  });

/**
 * Player tapped the Pay button while it was disabled by form-validity (not by
 * an in-flight payment). Direct evidence of pay intent blocked by validation —
 * not deduped, the count per session is itself meaningful (frustration depth).
 */
export const trackPayButtonBlockedClick = (context?: ICheckoutContext): void =>
  safe(() => {
    logInfo(InfoLogs.payButtonBlockedClick, null, buildLabels(context));
  });

/** Test-only escape hatch — DO NOT call from production code. */
export const __resetConversionTrackingForTests = (): void => {
  cardFormStartedFired = false;
  cardFormReadyToPayFired = false;
  applePayLoadedFired = false;
  expressWalletResolvedKeys.clear();
  cardFieldInvalidFields.clear();
  expressWalletReadyKeys.clear();
  expressWalletUnavailableKeys.clear();
};
